import { _js } from '@ifixit/utils/localize';
import styled, { css } from 'styled-components';
import { color, space, fontSize, lineHeight, borderRadius, breakpoint } from '@ifixit/primitives';
import PdfIcon from 'Guide/Icons/filetypes/32/Pdf';
import DocIcon from 'Guide/Icons/filetypes/32/Doc';
import XlsIcon from 'Guide/Icons/filetypes/32/Xls';
import PptIcon from 'Guide/Icons/filetypes/32/Ppt';
import FileIcon from 'Guide/Icons/filetypes/32/File';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@ifixit/ui/components/popover';
import { PropsWithChildren, useState, type JSX } from 'react';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';

type DisplayDocument = {
   url: string;
   metadataUrl: string;
   thumbnailUrl: string;
   downloadUrl: string;
   type: string;
   title: string;
};

type DocumentsProps = {
   documents: DisplayDocument[];
   view: CellViewType;
};

enum CellViewType {
   List = 'list',
   Gallery = 'gallery',
}

type CellProps = {
   $view: CellViewType;
};

const getIcon = (filetype: any) => {
   switch (filetype) {
      case 'word': {
         return DocIcon;
      }
      case 'excel': {
         return XlsIcon;
      }
      case 'powerpoint': {
         return PptIcon;
      }
      case 'pdf': {
         return PdfIcon;
      }
      default: {
         return FileIcon;
      }
   }
};

const MOBILE = `@media only screen and (max-width: ${breakpoint.sm})`;

const Cell = styled.div`
   display: flex;
   transition: background 0.1s;

   &:hover {
      text-decoration: none;
   }
`;

const cellTextStyle = css`
   font: normal bold ${fontSize.lg} / ${lineHeight.tight} var(--font-sans);
   color: ${color.gray[800]};
   overflow: hidden;
   text-overflow: ellipsis;
   display: -webkit-box;
   -webkit-box-orient: vertical;
   -webkit-line-clamp: 3;
   margin: 0;
`;

const ListCellText = styled.a`
   ${cellTextStyle}
   padding: 0 ${space[3]};
   flex-grow: 1;
`;

const MoreIconWrapper = styled.button`
   border: none;
   line-height: 0;
   flex-shrink: 0;
   display: flex;
   align-items: center;
   cursor: pointer;
`;

const ListMoreIconWrapper = styled(MoreIconWrapper)`
   background-color: transparent;
   height: 100%;
   color: ${color.gray[300]};
   transition: color 0.1s;
`;

const ListCell = styled(Cell)`
   flex: 0 1 50%;
   min-width: 0;

   padding: ${space[3]} ${space[2]};
   display: flex;
   align-items: center;

   &:hover {
      background-color: ${color.brand[50]};
      ${ListCellText} {
         text-decoration: none;
      }
      ${ListMoreIconWrapper} svg {
         color: ${color.gray[800]};
      }
   }

   ${MOBILE} {
      flex-basis: 100%;
   }
`;

const GalleryCellText = styled.span`
   ${cellTextStyle}
`;

const GalleryCellDiv = styled.div`
   position: absolute;
   bottom: 0;
   width: 100%;
   background-color: white;
   padding: ${space[4]} ${space[5]};
   z-index: 1;
   transition: background-color 0.1s;
   border-radius: 0px 0px ${borderRadius['md']} ${borderRadius['md']};
`;

const GalleryMoreIconWrapper = styled(MoreIconWrapper)`
   justify-content: center;
   position: absolute;
   top: ${space[2]};
   right: ${space[2]};
   width: ${space[5]};
   height: ${space[5]};
   z-index: 1;
   border-radius: ${borderRadius['md']};
   background-color: ${color.white};
   visibility: hidden;
   transition: visibility 0.1s;
   padding: 0px;
`;

const radiusCss = css`
   border-radius: ${borderRadius.lg};
   overflow: hidden;
`;

const hoverCss = css`
   .image-link::after {
      background-color: ${color.brand[200]};
   }
   ${GalleryCellDiv} {
      background-color: ${color.brand[50]};
   }
   ${GalleryMoreIconWrapper} {
      visibility: visible;
   }
`;

const GalleryCell = styled(Cell)<{ $hovered: boolean }>`
   position: relative;
   flex-basis: calc(25% - ${space[5]});
   flex-direction: column-reverse;
   margin: 0 0 ${space[5]} ${space[5]};
   min-width: 0;
   height: 200px;
   border: 1px solid ${color.gray[300]};
   ${radiusCss}
   background-clip: border-box;

   .image-link {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      overflow: hidden;

      &::after {
         content: '';
         position: absolute;
         top: 0;
         width: 100%;
         height: 100%;
         background-color: ${color.gray[200]};
         opacity: 0.75;
         transition: background-color 0.1s;
         ${radiusCss}
      }
   }

   &:hover,
   &:focus {
      ${hoverCss}
   }

   ${props => (props.$hovered ? hoverCss : null)}

   .image-link:hover {
      text-decoration: none;
   }

   ${MOBILE} {
      flex-basis: calc(100% - ${space[5]});
   }
`;

const GalleryFileIconImage = styled.svg`
   position: absolute;
   top: -21px;
   left: ${space[5]};
`;

const GalleryCellImage = styled.img`
   position: absolute;
   top: 0;
   min-width: 100%;
   ${radiusCss}

   ${MOBILE} {
      width: auto;
      margin: auto;
      left: 0;
      right: 0;
   }
`;

const Container = styled.div<CellProps>`
   display: flex;
   flex-wrap: wrap;

   ${props => props.$view === CellViewType.Gallery && `margin-left: -${space[5]};`}
`;

const DropdownText = styled.a`
   font: normal normal ${fontSize.md} / ${lineHeight.tight} var(--font-sans);
   color: ${color.gray[800]};
   padding: ${space[3]} ${space[4]};

   &:hover {
      background-color: ${color.gray[50]};
      text-decoration: none;
      border-radius: ${borderRadius['md']};
   }
`;

function Dropdown({
   content,
   children,
   onOpenChange,
}: PropsWithChildren<{
   content: JSX.Element | string;
   onOpenChange?: (open: boolean) => void;
}>) {
   return (
      <Popover onOpenChange={onOpenChange}>
         <PopoverTrigger asChild>{children}</PopoverTrigger>
         <PopoverContent side="bottom" className="w-auto p-2 bg-white text-black shadow-2xl">
            {content}
         </PopoverContent>
      </Popover>
   );
}

const DropdownContentsWrapper = styled.div`
   display: flex;
   flex-direction: column;
   text-align: left;
`;

const DropdownContent = ({ document }: { document: DisplayDocument }) => (
   <DropdownContentsWrapper>
      <DropdownText href={document.url}>{_js('View')}</DropdownText>
      <DropdownText href={document.metadataUrl}>{_js('History')}</DropdownText>
      {/* No `download` attribute: the file host's Content-Disposition
          triggers the save, and for logged-out visitors downloadUrl is the
          document page, which the attribute would save as a file instead
          of rendering. */}
      <DropdownText
         role="button"
         href={document.downloadUrl}
         onClick={() => {
            trackAnalyticsCustomEvent({
               eventCategory: 'Document',
               eventAction: 'Document - Download',
               eventName: document.downloadUrl,
            });
         }}
      >
         {_js('Download')}
      </DropdownText>
   </DropdownContentsWrapper>
);

const IconLink = styled.a`
   flex-shrink: 0;
`;

/* eslint-disable react-hooks/static-components */
const ListDocument = ({ document }: { document: DisplayDocument }) => {
   const Icon = getIcon(document.type);
   return (
      <ListCell>
         <IconLink href={document.url}>
            <Icon />
         </IconLink>
         <ListCellText href={document.url}>{document.title}</ListCellText>
         <Dropdown content={<DropdownContent document={document} />}>
            <ListMoreIconWrapper>
               <FontAwesomeIcon icon={faEllipsis} />
            </ListMoreIconWrapper>
         </Dropdown>
      </ListCell>
   );
};
/* eslint-enable react-hooks/static-components */

const GalleryDocument = ({ document }: { document: DisplayDocument }) => {
   // eslint-disable-next-line @eslint-react/no-nested-component-definitions -- TODO: grandfathered in eslint-plugin-react -> @eslint-react migration
   const Icon = (props: any) => {
      const I = getIcon(document.type);
      return <I {...props} />;
   };

   const [isOpen, setIsOpen] = useState(false);

   return (
      <GalleryCell $hovered={isOpen}>
         <a className="image-link" href={document.url}>
            <GalleryCellImage src={document.thumbnailUrl} alt={document.title} />
            <GalleryCellDiv>
               <GalleryFileIconImage as={Icon} />
               <GalleryCellText>{document.title}</GalleryCellText>
            </GalleryCellDiv>
         </a>
         <Dropdown content={<DropdownContent document={document} />} onOpenChange={setIsOpen}>
            <GalleryMoreIconWrapper>
               <FontAwesomeIcon icon={faEllipsis} />
            </GalleryMoreIconWrapper>
         </Dropdown>
      </GalleryCell>
   );
};

const Documents = ({ documents, view }: DocumentsProps) => (
   <Container $view={view}>
      {documents.map((document: DisplayDocument) =>
         view === CellViewType.List ? (
            <ListDocument key={document.url} document={document} />
         ) : (
            <GalleryDocument key={document.url} document={document} />
         )
      )}
   </Container>
);

export { DisplayDocument, CellViewType };
export default Documents;
